import { isRef, traverse, SMALL_FACTOR, getByArray, setByArray, set3d, EMPTY_SCHEMA } from './utils'
import type { MaterializerFactory, Ref, InvalidationList } from './types'
import _ from 'lodash'

export const createSimpleMaterializer: MaterializerFactory = ({ transform } = {}) => {
	const templates: Record<string, Record<string, Record<string, any>>> = {}
	const cache: Record<string, Record<string, Record<string, any>>> = {}

	const resolveRef = (val: Ref<any>) => {
		let resolved
		if (val.refPath.length === 3) {
			resolved = get(val.refPath)
		} else {
			const [key1, key2, key3, ...rest] = val.refPath
			const node = get([key1, key2, key3])
			resolved = rest.length && typeof node !== 'undefined' ? getByArray(node, rest) : node
		}

		if (typeof resolved === 'undefined') {
			return val.defaultValue
		}
		return resolved
	}
	const get = (path: Array<string>) => {
		if (_.has(cache, path)) {
			return getByArray(cache, path)
		}

		const maybeTemplate = getByArray(templates, path)
		if (typeof maybeTemplate === 'undefined') {
			return undefined
		}

		const { template, schema } = maybeTemplate

		const nodeSchema = schema
		if (nodeSchema === EMPTY_SCHEMA || isPrimitive(template)) {
			const transformedValue = transform ? transform(template, path) : template

			setByArray(cache, path, transformedValue)
			return transformedValue
		}

		let newVal = {}
		traverse(
			template,
			(objValue, objPath) => {
				if (isRef(objValue)) {
					const resolved = resolveRef(objValue)
					if (objPath.length > 0) {
						setByArray(newVal, objPath, resolved)
					} else {
						newVal = resolved
					}
					return true // exit traverse
				}
				if (Array.isArray(objValue)) {
					setByArray(newVal, objPath, [])
					return
				}
				if (objPath.length > 0) {
					setByArray(newVal, objPath, objValue)
				}
			},
			SMALL_FACTOR
		)
		const transformedValue = transform ? transform(newVal, path) : newVal

		setByArray(cache, path, transformedValue)
		return transformedValue
	}

	return {
		update() {
			throw new Error('Simple materializer does not support updates')
		},
		batch(batchFunction, _runAndReport = (fn) => fn()) {
			const allInvalidations: InvalidationList = []
			batchFunction((key1, key2, key3, template, schema) => {
				set3d(templates, key1, key2, key3!, { template, schema })

				allInvalidations.push([key1, key2, key3!])
			})
			return allInvalidations
		},
		get,
	}
}
const isPrimitive = (value: any) => {
	switch (typeof value) {
		case 'object':
			return value === null
		case 'function':
			return false
		default:
			return true
	}
}
